import {
  AloftPrice,
  CraftsmanAdjustmentProps,
  getCraftsmanAdjustment,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectAdjustmentSettings, selectCompSequence, selectCompTypes } from '../orderSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectDepreciationRatiosForFeature } from '../selectDepreciationRatioForFeature.ts';
import { selectDwellingCostNew, selectDwellingCostNews } from '../selectDwellingCostNew.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectAdjustmentProps } from './selectAdjustmentProps.ts';
import { applyRoundingAndThresholds } from './utils/applyRoundingAndThresholds.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateConditionAdj = (
  adjustmentProps: CraftsmanAdjustmentProps,
  compDwellingCostNew: AloftPrice | undefined,
  depreciationRatios: ReturnType<typeof selectDepreciationRatiosForFeature>,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
) => {
  if (isUndefined(compDwellingCostNew) || isUndefined(depreciationRatios)) {
    return undefined;
  }

  let depreciationRatio = depreciationRatios.subject;
  if (compTypes[compID] === 'arv') {
    depreciationRatio = depreciationRatios.subjectArv;
  }
  const compDepreciationRatio = depreciationRatios[compID];

  if (isUndefined(depreciationRatio) || isUndefined(compDepreciationRatio)) {
    return undefined;
  }

  const adjCalculator = getCraftsmanAdjustment(adjustmentProps);

  return adjCalculator.getConditionAdj({
    comp: {
      replacementCostNew: compDwellingCostNew,
    },
    depreciationRatio,
    compDepreciationRatio,
  }).adjustment;
};

const selectConditionAdj = createPriceSelector(
  [
    selectAdjustmentProps,
    selectDwellingCostNew,
    (state) => selectDepreciationRatiosForFeature(state, undefined),
    (_state, id: string) => id,
    selectCompTypes,
  ],
  calculateConditionAdj,
);

export const selectConditionAdjs = createSelector(
  [
    selectAdjustmentProps,
    selectDwellingCostNews,
    (state) => selectDepreciationRatiosForFeature(state, undefined),
    selectCompSequence,
    selectCompTypes,
  ],
  (adjustmentProps, dwellingCostNews, depreciationRatios, compSequence, compTypes) => {
    return compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateConditionAdj(
        adjustmentProps,
        dwellingCostNews[propID],
        depreciationRatios,
        propID,
        compTypes,
      );

      if (isDefined(adj)) acc[propID] = adj;

      return acc;
    }, {});
  },
);

const selectAppliedConditionAdj = createPriceSelector(
  [selectConditionAdj, selectAdjustmentSettings],
  (adj, adjSettings) => {
    if (isUndefined(adj)) {
      return undefined;
    }

    return applyRoundingAndThresholds(adj, 'condition', adjSettings);
  },
);

export const selectAppliedConditionAdjDisplay = createSelector(
  [selectAppliedConditionAdj],
  (adj) => {
    if (isUndefined(adj)) {
      return undefined;
    }

    return {
      value: getStringValueFromAdjustment(adj),
      color: getColorFromAdjustment(adj),
    };
  },
);

export const selectAppliedConditionAdjsDisplay = createSelector(
  [
    selectCompSequence,
    selectConditionAdjs,
    selectAdjustmentSettings,
    selectManualAdjustmentsEntities,
  ],
  (compSequence, conditionAdjs, adjSettings, manualAdjustmentsEntities) => {
    return compSequence.reduce((acc: Record<string, { value: string; color: string }>, propID) => {
      const adj = conditionAdjs[propID];
      const manualAdjustment = manualAdjustmentsEntities[propID]?.condition;

      if (isDefined(manualAdjustment)) {
        const manualAdjustmentPrice = AloftPrice.schema.parse(manualAdjustment);
        acc[propID] = {
          value: getStringValueFromAdjustment(manualAdjustmentPrice) + '*',
          color: getColorFromAdjustment(manualAdjustmentPrice),
        };
      } else if (isDefined(adj)) {
        acc[propID] = {
          value: getStringValueFromAdjustment(
            applyRoundingAndThresholds(adj, 'condition', adjSettings),
          ),
          color: getColorFromAdjustment(adj),
        };
      }

      return acc;
    }, {});
  },
);
